import moment from "moment"
import React, { useEffect, useImperativeHandle, useMemo, useRef, useState, forwardRef } from "react"
import { Pressable, View, findNodeHandle, UIManager } from "react-native"
import { ActivityIndicator, SegmentedButtons } from "react-native-paper"
import { Rating } from "@kolking/react-native-rating"
import FontAwesome5 from "react-native-vector-icons/FontAwesome5"

import { IS_WEB } from "@common/utils/mobileUtils"
import AvatarM from "@common/components/avatar/AvatarM"
import TextM from "@common/components/text/TextM"
import FilledButtonM from "@common/components/button/FilledButtonM"
import TextButtonM from "@common/components/button/TextButtonM"
import { ErrorBanner } from "@common/components/alert/ErrorBanner"
import AutoSaveTextInputM from "@common/components/input/TextInputAutoSaveM"
import TagChipsSelector from "@common/components/TagChipsSelector"
import { AudioRecordingContainer } from "../../../student/activity/execute/AudioRecordingContainer"
import { ALERT_LABELS, AlertBadge, hasCharterBreach } from "@common/liveMode/QualityClosureScreen"
import { theme } from "@common/theme/theme"
import { connect } from "react-redux"
import { apiRequest } from "@data/redux/actions/api"
import { liveReportService } from "@data/services/liveReportService"
import { useRequestsBatch } from "@common/hooks/useRequestsBatch"
import useOwnApiAnswer from "@common/hooks/useOwnApiAnswer"
import { formIds } from "@data/constants/formConstants"
import debounce from "lodash.debounce"

// Backend report-level statistics.scores has shape:
//   { assiduity: {raw_score, score_max, details}, interaction: {...},
//     productivity: {...}, total_score: <0-100> }
// Backend occurrence-level statistics.students_scores[sid] has shape:
//   { assiduity: <0-40>, interaction: <0-30>, productivity: <0-30>, total_score }
// readAxisData() below accepts both.

const AXES = [
    { key: "assiduity", name: "Présence", color: theme.colors.success, max: 40 },
    { key: "interaction", name: "Participation", color: theme.colors.phone, max: 30 },
    { key: "productivity", name: "Productivité", color: theme.colors.purple, max: 30 },
]

const SCORE_COLOR = (totalScore) => {
    if (totalScore >= 60) return theme.colors.success
    if (totalScore >= 40) return theme.colors.warning
    return theme.colors.error
}

const readAxisData = (scoresLike, axisKey, max) => {
    const raw = scoresLike?.[axisKey]
    if (raw == null) return { value: 0, max, details: [] }
    if (typeof raw === "number") return { value: raw, max, details: [] }
    return {
        value: raw.raw_score ?? raw.score ?? 0,
        max: raw.score_max ?? max,
        details: raw.details ?? [],
    }
}

const COMMENT_TYPES = {
    Vocal: "Vocal",
    Texte: "Texte",
}

// Where the comment stands on the server; null once nothing is pending. Only
// the voice is "sending": the text autosaves as the tutor types.
const COMMENT_SEND = {
    sending: "sending",
    failed: "failed",
}

// A refusal (422) carries the sentence to show on its field — its top-level
// `message` is Laravel's generic « Les données sont invalides. ». Anything else
// (500, network) gets ours. The comment stays on the card, so retrying costs nothing.
const sendErrorMessage = (error, fallback) =>
    (error?.status === 422 && Object.values(error?.errors ?? {})[0]?.[0]) || fallback
const VOICE_NOT_SENT = "Ton vocal n'a pas été envoyé. Il est toujours là : réessaie."
const TEXT_NOT_SAVED = "Ton commentaire n'a pas été enregistré. Il est toujours là : réessaie."

// The tutor's voice note, unless its transcription came back empty (a silent
// or inaudible file): that one says nothing to the family, so the card treats
// it as no comment at all and asks for a new one.
const usableCommentRecord = (liveReport) =>
    liveReport?.comment_record_untranscribed ? null : (liveReport?.comment_record ?? null)

// Match legacy EndSessionStudentComment criteria (hasContent && hasRating):
//   tutor_rating > 0 AND (tutor_comment non-empty OR a usable comment_record)
export const isBilanFait = (liveReport) => {
    if (!liveReport?.statistics) return false
    if (!(liveReport.tutor_rating > 0)) return false
    const hasComment =
        !!liveReport.tutor_comment &&
        liveReport.tutor_comment.trim() !== ""
    const hasRecording = !!usableCommentRecord(liveReport)
    return hasComment || hasRecording
}

// ── Centered rating + grid tags, titled section ──────────────────────────

const RatingAndTagsSection = connect(
    (state) => ({
        tutorTagsPositive: state.data.staticData?.tutor_tags_positive ?? {},
        tutorTagsNegative: state.data.staticData?.tutor_tags_negative ?? {},
    }),
    { apiRequest }
)(
    ({
        liveReportId,
        firstName,
        initialRating,
        initialTags,
        tutorTagsPositive,
        tutorTagsNegative,
        apiRequest,
        onChange,
    }) => {
        const [rating, setRating] = useState(initialRating || 0)
        const normalize = (tags) =>
            !tags || !Array.isArray(tags)
                ? []
                : tags.map((t) => (typeof t === "object" ? t.key : t))
        const [tags, setTags] = useState(normalize(initialTags))

        const debouncedSave = useMemo(
            () =>
                debounce((r, t) => {
                    apiRequest(
                        liveReportService.sendStudentComment,
                        { id: liveReportId },
                        { tutor_rating: r || null, tutor_tags: t }
                    )
                }, 500),
            [liveReportId, apiRequest]
        )
        useEffect(() => () => debouncedSave.cancel(), [debouncedSave])

        const handleRating = (value) => {
            const newTags = []
            setTags(newTags)
            setRating(value)
            debouncedSave(value, newTags)
            onChange?.(value, newTags)
        }
        const toggleTag = (key) => {
            const next = tags.includes(key) ? tags.filter((t) => t !== key) : [...tags, key]
            setTags(next)
            debouncedSave(rating, next)
            onChange?.(rating, next)
        }

        const isPositive = rating >= 4
        const currentTags = isPositive ? tutorTagsPositive : tutorTagsNegative
        const tagsArray = Object.entries(currentTags).map(([key, t]) => ({
            key,
            label: t.label,
            emoji: t.emoji,
        }))

        const tagsTitle = isPositive
            ? "Qu'est-ce que tu as apprécié le plus ?"
            : "Quelles ont été les difficultés ?"

        return (
            <View style={{ alignItems: "center", paddingVertical: 4 }}>
                <TextM
                    fontSize={14}
                    fontWeight="Bold"
                    color={theme.colors.primary}
                    center
                    wrap
                    style={{ marginBottom: 10 }}
                >
                    {`Qu'as-tu pensé de ce live avec ${firstName} ?`}
                </TextM>
                <Rating
                    maxRating={5}
                    rating={rating}
                    size={28}
                    spacing={6}
                    fillColor={theme.colors.accent}
                    touchColor={theme.colors.material.secondary["700"]}
                    onChange={handleRating}
                />
                {rating > 0 && tagsArray.length > 0 && (
                    <View style={{ width: "100%", marginTop: 14 }}>
                        <TextM
                            fontSize={12}
                            color={theme.colors.grey}
                            center
                            wrap
                            style={{ marginBottom: 8 }}
                        >
                            {tagsTitle}
                        </TextM>
                        <TagChipsSelector
                            tags={tagsArray}
                            selectedTags={tags}
                            onTagToggle={toggleTag}
                            small
                        />
                    </View>
                )}
            </View>
        )
    }
)

// ── Compact comment block with VOCAL/TEXTE toggle ─────────────────────────
// Stays mounted across collapse/expand so local edits (including erasing the
// whole textarea) survive when the parent toggles the expanded view.

const CommentBlock = connect(
    (state, ownProps) => ({
        deleteCommentRecordRequestId: state.api.deleteCommentRecord?.routeParams?.id,
    }),
    { apiRequest }
)(({ liveReport, firstName, isTrial, onContentChange, onSendChange, apiRequest }) => {
    const student = liveReport?.student
    const [selectedType, setSelectedType] = useState(
        liveReport?.tutor_comment ? COMMENT_TYPES.Texte : COMMENT_TYPES.Vocal
    )
    const [recordingURI, setRecordingURI] = useState(
        usableCommentRecord(liveReport)
    )
    // Shown until the tutor records again or writes the comment.
    const [untranscribed, setUntranscribed] = useState(
        !!liveReport?.comment_record_untranscribed && !liveReport?.tutor_comment
    )
    const [tutorComment, setTutorComment] = useState(
        liveReport?.tutor_comment ?? ""
    )
    // { message, retry: "text" | "voice" } of the last send that failed, until one succeeds.
    const [sendError, setSendError] = useState(null)
    const sendSettled = () => {
        setSendError(null)
        onSendChange?.(null)
    }
    const sendFailed = (message, retry) => {
        setSendError({ message, retry })
        onSendChange?.(COMMENT_SEND.failed)
    }

    // The rating saves on the same api slot: hear the answer to the text only.
    const saveText = (text) =>
        sendText({ id: liveReport?.id }, { tutor_comment: text })
    const sendText = useOwnApiAnswer(
        liveReportService.sendStudentComment,
        sendSettled,
        (error) => sendFailed(sendErrorMessage(error, TEXT_NOT_SAVED), "text")
    )

    const handleTextChange = (v) => {
        setUntranscribed(false)
        setTutorComment(v)
        saveText(v)
        onContentChange?.(v)
    }

    const audioFile = {
        uri: recordingURI,
        name: `student_comment${student?.display_name}_${moment().format(
            "YYYY-MM-DD"
        )}.m4a`,
        type: "audio/m4a",
    }
    const filesToUpload = [audioFile]

    const [, submitted, startRequests] = useRequestsBatch(
        [],
        filesToUpload,
        formIds.LIVE_REPORT_COMMENT + "_" + student?.id,
        {
            service: isTrial
                ? liveReportService.setStudentDiagnostic
                : liveReportService.sendStudentComment,
            tmpFilesKey: "comment_record",
            tmpFilesFormatter: (attachmentTmp) => attachmentTmp,
            additionalData: [],
            data: { tutor_comment: tutorComment },
            params: { id: liveReport?.id },
            onSuccessBeforeNext: sendSettled,
            // A failed upload or a refused voice: the recording stays on the
            // card and is sent again from there.
            onError: (error) => sendFailed(sendErrorMessage(error, VOICE_NOT_SENT), "voice"),
        },
        []
    )
    const sendVoice = () => {
        setSendError(null)
        onSendChange?.(COMMENT_SEND.sending)
        startRequests()
    }

    const [initialRender, setInitialRender] = useState(true)
    useEffect(() => {
        if (initialRender) {
            setInitialRender(false)
            return
        }
        if (submitted) return
        if (recordingURI) {
            setUntranscribed(false)
            sendVoice()
            onContentChange?.(tutorComment, recordingURI)
        }
    }, [recordingURI])

    const handleTypeChange = (newType) => {
        if (newType === selectedType) return
        const hasExistingContent =
            (selectedType === COMMENT_TYPES.Vocal && recordingURI) ||
            (selectedType === COMMENT_TYPES.Texte && tutorComment)
        if (!hasExistingContent) {
            setSelectedType(newType)
            return
        }
        if (selectedType === COMMENT_TYPES.Vocal) {
            apiRequest(liveReportService.deleteCommentRecord, { id: liveReport.id })
            setRecordingURI(null)
        } else {
            apiRequest(
                liveReportService.sendStudentComment,
                { id: liveReport?.id },
                { tutor_comment: "" }
            )
            setTutorComment("")
        }
        sendSettled()
        onContentChange?.("")
        setSelectedType(newType)
    }

    return (
        <View style={{ marginTop: 4 }}>
            <View style={{ alignItems: "center" }}>
                <TextM
                    fontSize={14}
                    fontWeight="Bold"
                    color={theme.colors.primary}
                    center
                    wrap
                >
                    {`Fais un compte-rendu pour ${firstName}`}
                </TextM>
                <TextM
                    fontSize={12}
                    color={theme.colors.grey}
                    center
                    wrap
                    style={{ marginTop: 4, marginBottom: 10 }}
                >
                    Il sera utilisé pour rédiger un bilan de session aux parents
                </TextM>
            </View>
            {untranscribed ? (
                <ErrorBanner
                    message={`Ton vocal pour ${firstName} est arrivé vide : on n'a rien pu en tirer. Enregistre-le de nouveau, ou écris-le.`}
                    style={{ marginBottom: 8 }}
                />
            ) : null}
            {selectedType === COMMENT_TYPES.Vocal ? (
                submitted ? (
                    <View style={{ alignItems: "center", paddingVertical: 8 }}>
                        <ActivityIndicator
                            animating
                            color={theme.colors.primary}
                        />
                    </View>
                ) : (
                    <AudioRecordingContainer
                        recordingURI={recordingURI}
                        setRecordingURI={setRecordingURI}
                        minRecordingDurationMs={3000}
                        onDeleteRecording={() => {
                            apiRequest(liveReportService.deleteCommentRecord, {
                                id: liveReport.id,
                            })
                            sendSettled()
                            onContentChange?.(tutorComment, null)
                        }}
                    />
                )
            ) : (
                <AutoSaveTextInputM
                    placeholder={`Attitude, difficultés, progrès, points forts…`}
                    name={`student-comment-${liveReport?.id}`}
                    defaultValue={tutorComment ?? ""}
                    contentContainerStyle={{ width: "100%" }}
                    style={{
                        minHeight: 110,
                        maxHeight: 220,
                        backgroundColor: theme.colors.white,
                    }}
                    multiline
                    numberOfLines={5}
                    onChangeText={handleTextChange}
                />
            )}
            {sendError ? (
                <View style={{ alignItems: "center" }}>
                    <ErrorBanner message={sendError.message} style={{ marginBottom: 4 }} />
                    <TextButtonM
                        label="Réessayer"
                        fontWeight="Bold"
                        color={theme.colors.primary}
                        onPress={() =>
                            sendError.retry === "text" ? saveText(tutorComment) : sendVoice()
                        }
                    />
                </View>
            ) : null}
            <View style={{ alignSelf: "center", marginTop: 8 }}>
                <SegmentedButtons
                    value={selectedType}
                    onValueChange={handleTypeChange}
                    density="small"
                    style={{ width: 130 }}
                    buttons={[
                        {
                            value: COMMENT_TYPES.Vocal,
                            icon: "microphone",
                            showSelectedCheck: false,
                            label: "",
                        },
                        {
                            value: COMMENT_TYPES.Texte,
                            icon: "keyboard",
                            showSelectedCheck: false,
                            label: "",
                        },
                    ]}
                />
            </View>
        </View>
    )
})

// ── Engagement bar with tappable segments ─────────────────────────────────

const TappableEngagementBar = ({ scoresLike, totalScore, productivityPending, expandedAxisKey, onSelectAxis }) => {
    const totalColor = SCORE_COLOR(totalScore ?? 0)
    return (
        <View style={{ marginBottom: 6 }}>
            <View
                style={{
                    flexDirection: "row",
                    justifyContent: "space-between",
                    alignItems: "baseline",
                    marginBottom: 3,
                }}
            >
                <TextM fontSize={11} color={theme.colors.grey}>
                    Score d&apos;engagement
                </TextM>
                <TextM fontWeight="Bold" fontSize={13} color={totalColor}>
                    {/* Le total contient la productivité : l'afficher pendant
                        le calcul donnerait un /100 qui bougera. */}
                    {productivityPending
                        ? "—"
                        : totalScore != null
                          ? `${Math.round(totalScore)}/100`
                          : "—"}
                </TextM>
            </View>
            <View
                style={{
                    flexDirection: "row",
                    height: 6,
                    backgroundColor: theme.colors.lighter,
                    borderRadius: 3,
                    overflow: "hidden",
                    marginBottom: 4,
                }}
            >
                {AXES.map((axis) => {
                    const a = readAxisData(scoresLike, axis.key, axis.max)
                    // Rien plutôt qu'un segment faux : une barre remplie sur un
                    // score pas encore calculé se lit comme un résultat.
                    const pending =
                        productivityPending && axis.key === "productivity"
                    const widthPct = pending
                        ? 0
                        : Math.max(0, Math.min(100, a.value))
                    return (
                        <View
                            key={axis.key}
                            style={{
                                width: `${widthPct}%`,
                                height: "100%",
                                backgroundColor: axis.color,
                            }}
                        />
                    )
                })}
            </View>
            <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 4 }}>
                {AXES.map((axis) => {
                    const a = readAxisData(scoresLike, axis.key, axis.max)
                    const active = expandedAxisKey === axis.key
                    return (
                        <Pressable
                            key={axis.key}
                            onPress={() => onSelectAxis(active ? null : axis.key)}
                            style={{
                                flexDirection: "row",
                                alignItems: "center",
                                paddingVertical: 3,
                                paddingHorizontal: 7,
                                borderRadius: 12,
                                backgroundColor: active
                                    ? theme.colors.primaryLighter
                                    : "transparent",
                            }}
                        >
                            <View
                                style={{
                                    width: 7,
                                    height: 7,
                                    borderRadius: 4,
                                    backgroundColor: axis.color,
                                    marginRight: 4,
                                }}
                            />
                            <TextM fontSize={11} color={theme.colors.grey}>
                                {axis.name}
                            </TextM>
                            {productivityPending && axis.key === "productivity" ? (
                                <TextM
                                    fontSize={10}
                                    color={theme.colors.grey}
                                    style={{ marginLeft: 3, fontStyle: "italic" }}
                                >
                                    calcul en cours…
                                </TextM>
                            ) : (
                                <>
                                    <TextM
                                        fontWeight="Bold"
                                        fontSize={11}
                                        color={axis.color}
                                        style={{ marginLeft: 3 }}
                                    >
                                        {Math.round(a.value)}
                                    </TextM>
                                    <TextM fontSize={10} color={theme.colors.light}>
                                        /{a.max}
                                    </TextM>
                                </>
                            )}
                            <FontAwesome5
                                name={active ? "chevron-up" : "chevron-down"}
                                size={9}
                                color={theme.colors.light}
                                style={{ marginLeft: 4 }}
                            />
                        </Pressable>
                    )
                })}
            </View>
        </View>
    )
}

// ── Axis drill-down panel (score split only, never alerts) ────────────────

const AxisDrillDown = ({ axisKey, scoresLike, productivityPending, productivityFailed }) => {
    const axis = AXES.find((a) => a.key === axisKey)
    if (!axis) return null
    const a = readAxisData(scoresLike, axisKey, axis.max)

    // Le même score s'affiche à deux endroits : la pastille et ce panneau.
    // Traiter l'attente uniquement sur la pastille était pire que de ne rien
    // faire — le tuteur lisait « calcul en cours », dépliait pour voir le
    // détail (le geste naturel), et retombait sur « 0/30 — aucune activité
    // soumise ». L'écran se contredisait lui-même.
    const pending = productivityPending && axisKey === "productivity"
    const failed = productivityFailed && axisKey === "productivity"

    return (
        <View
            style={{
                marginTop: -2,
                marginBottom: 6,
                paddingVertical: 8,
                paddingHorizontal: 10,
                backgroundColor: theme.colors.primaryLighter,
                borderRadius: 8,
            }}
        >
            <View
                style={{
                    flexDirection: "row",
                    alignItems: "center",
                    marginBottom: 4,
                }}
            >
                <View
                    style={{
                        width: 7,
                        height: 7,
                        borderRadius: 4,
                        backgroundColor: axis.color,
                        marginRight: 6,
                    }}
                />
                <TextM
                    fontWeight="Bold"
                    fontSize={12}
                    color={theme.colors.dark}
                    style={{ flex: 1 }}
                >
                    {axis.name}
                </TextM>
                {pending ? (
                    <TextM
                        fontSize={11}
                        color={theme.colors.grey}
                        style={{ fontStyle: "italic" }}
                    >
                        calcul en cours…
                    </TextM>
                ) : (
                    <TextM fontWeight="Bold" fontSize={12} color={axis.color}>
                        {Math.round(a.value)}/{a.max}
                    </TextM>
                )}
            </View>

            {pending ? (
                <TextM fontSize={11} color={theme.colors.grey} wrap>
                    Le score oral est en cours de calcul. Le détail par critère
                    s'affichera dès qu'il sera disponible.
                </TextM>
            ) : failed ? (
                <TextM fontSize={11} color={theme.colors.warning} wrap>
                    Score oral indisponible — le score ci-dessus ne reflète que
                    le travail écrit.
                </TextM>
            ) : a.details.length > 0 ? (
                a.details.map((d, i) => {
                    const contextLine =
                        d.value != null
                            ? `${d.value}${d.unity ? ` ${d.unity}` : ""}`
                            : null
                    return (
                        <View key={i} style={{ marginTop: i === 0 ? 0 : 4 }}>
                            <View
                                style={{
                                    flexDirection: "row",
                                    justifyContent: "space-between",
                                }}
                            >
                                <TextM
                                    fontSize={11}
                                    color={theme.colors.grey}
                                    wrap
                                    style={{ flex: 1 }}
                                >
                                    {d.name}
                                </TextM>
                                <TextM
                                    fontSize={11}
                                    color={theme.colors.dark}
                                    fontWeight="SemiBold"
                                >
                                    {d.score != null
                                        ? `${d.score}/${d.score_max ?? "?"}`
                                        : "—"}
                                </TextM>
                            </View>
                            {contextLine ? (
                                <TextM
                                    fontSize={10}
                                    color={theme.colors.light}
                                    wrap
                                    style={{ marginTop: 1 }}
                                >
                                    {contextLine}
                                </TextM>
                            ) : null}
                        </View>
                    )
                })
            ) : (
                <TextM fontSize={12} color={theme.colors.grey} wrap>
                    Détail indisponible.
                </TextM>
            )}
        </View>
    )
}

// ── Collapsible alert section ─────────────────────────────────────────────

const CollapsibleAxisAlerts = ({ axis }) => {
    const isBreach = hasCharterBreach(axis.alerts)
    const color = isBreach ? theme.colors.error : theme.colors.warning
    const [expanded, setExpanded] = useState({})

    const toggle = (idx) =>
        setExpanded((prev) => ({ ...prev, [idx]: !prev[idx] }))

    return (
        <View style={{ marginTop: 6 }}>
            <View
                style={{
                    flexDirection: "row",
                    alignItems: "center",
                    marginBottom: 4,
                }}
            >
                <FontAwesome5
                    name={isBreach ? "exclamation-circle" : "exclamation-triangle"}
                    size={12}
                    color={color}
                    solid
                />
                <TextM
                    fontSize={12}
                    fontWeight="SemiBold"
                    color={theme.colors.dark}
                    style={{ marginLeft: 6, flex: 1 }}
                >
                    {axis.name}
                </TextM>
            </View>
            <View
                style={{
                    flexDirection: "row",
                    flexWrap: "wrap",
                    gap: 6,
                    marginLeft: 18,
                }}
            >
                {(axis.alerts ?? []).map((alert, i) => {
                    const breach = alert.charter_breach
                    const chipLabel = breach
                        ? alert.charter_rule ?? "Charte"
                        : ALERT_LABELS[alert.indicator] ?? "Attention"
                    const chipColor = breach ? theme.colors.error : theme.colors.warning
                    const chipBg = breach ? theme.colors.errorLight : theme.colors.warningLight
                    const isOpen = !!expanded[i]
                    return (
                        <Pressable key={i} onPress={() => toggle(i)}>
                            <View
                                style={{
                                    flexDirection: "row",
                                    alignItems: "center",
                                    backgroundColor: chipBg,
                                    borderRadius: 6,
                                    paddingHorizontal: 6,
                                    paddingVertical: 3,
                                    borderWidth: isOpen ? 1 : 0,
                                    borderColor: chipColor,
                                }}
                            >
                                <FontAwesome5
                                    name={
                                        breach
                                            ? "file-contract"
                                            : "exclamation-triangle"
                                    }
                                    size={9}
                                    color={chipColor}
                                />
                                <TextM
                                    fontWeight="Bold"
                                    fontSize={10}
                                    color={chipColor}
                                    style={{ marginLeft: 3 }}
                                >
                                    {chipLabel}
                                </TextM>
                                <FontAwesome5
                                    name={isOpen ? "chevron-up" : "chevron-down"}
                                    size={8}
                                    color={chipColor}
                                    style={{ marginLeft: 4 }}
                                />
                            </View>
                        </Pressable>
                    )
                })}
            </View>
            {(axis.alerts ?? []).map((alert, i) => {
                if (!expanded[i]) return null
                const breach = alert.charter_breach
                return (
                    <View
                        key={`msg-${i}`}
                        style={{
                            marginTop: 6,
                            marginLeft: 18,
                            paddingVertical: 6,
                            paddingHorizontal: 10,
                            backgroundColor: theme.colors.lighter,
                            borderRadius: 6,
                            borderLeftWidth: 3,
                            borderLeftColor: breach
                                ? theme.colors.error
                                : theme.colors.warning,
                        }}
                    >
                        {alert.recurrence_count >= 2 && (
                            <View style={{ marginBottom: 4 }}>
                                <AlertBadge
                                    icon="redo"
                                    label={`${alert.recurrence_count}ème session consécutive`}
                                    color={theme.colors.error}
                                    bgColor={theme.colors.errorLight}
                                />
                            </View>
                        )}
                        <TextM
                            fontSize={12}
                            color={
                                breach ? theme.colors.errorDark : theme.colors.grey
                            }
                            wrap
                        >
                            {alert.message}
                        </TextM>
                    </View>
                )
            })}
        </View>
    )
}

// ── Header severity badges ────────────────────────────────────────────────
// Count CHIPS (one per alert), not axes. A single axis can mix charter
// breaches and plain attentions, so we tally them separately and render up
// to two compact pills.

const SingleSeverityBadge = ({ icon, count, labelSingular, labelPlural, color, bg }) => (
    <View
        style={{
            flexDirection: "row",
            alignItems: "center",
            backgroundColor: bg,
            paddingHorizontal: 7,
            paddingVertical: 3,
            borderRadius: 10,
        }}
    >
        <FontAwesome5
            name={icon}
            size={9}
            color={color}
            solid
            style={{ marginRight: 4 }}
        />
        <TextM fontSize={10} fontWeight="Bold" color={color}>
            {count} {count > 1 ? labelPlural : labelSingular}
        </TextM>
    </View>
)

const HeaderSeverityBadge = ({ nonOkAxes }) => {
    let attentionCount = 0
    let manquementCount = 0
    ;(nonOkAxes ?? []).forEach((axis) => {
        ;(axis.alerts ?? []).forEach((alert) => {
            if (alert.charter_breach) manquementCount += 1
            else attentionCount += 1
        })
    })
    if (attentionCount === 0 && manquementCount === 0) return null

    return (
        <View
            style={{
                flexDirection: "row",
                alignItems: "center",
                gap: 4,
            }}
        >
            {attentionCount > 0 && (
                <SingleSeverityBadge
                    icon="exclamation-triangle"
                    count={attentionCount}
                    labelSingular="point d'attention"
                    labelPlural="points d'attention"
                    color={theme.colors.warning}
                    bg={theme.colors.warningLight}
                />
            )}
            {manquementCount > 0 && (
                <SingleSeverityBadge
                    icon="exclamation-circle"
                    count={manquementCount}
                    labelSingular="alerte"
                    labelPlural="alertes"
                    color={theme.colors.error}
                    bg={theme.colors.errorLight}
                />
            )}
        </View>
    )
}

// ── Main card ─────────────────────────────────────────────────────────────

const StudentCloseOutCard = forwardRef(
    (
        {
            liveReport,
            qualityStudent,
            isTrial,
            oralScoringEnabled,
            missionPrescription,
            onBilanFaitChange,
            onDone,
        },
        ref
    ) => {
        const student = liveReport?.student
        const firstName = student?.display_name ?? student?.firstname ?? "l'élève"
        const isConnected = !!liveReport?.statistics

        // La productivité est recalculée en asynchrone après la finalisation
        // (40-120 s mesurées en séance réelle). Tant que le scoring n'a pas
        // atterri, la valeur en base est le score ÉCRIT SEUL — soit 0 pour un
        // élève qui n'a rien soumis. L'afficher tel quel annonce « 0/30 —
        // aucune activité » comme un fait, alors que le vrai score arrive.
        //
        // `oral_productivity` est le marqueur d'atterrissage : le job l'écrit
        // même quand l'élève n'a presque pas parlé (score oral à 0, alpha à 0).
        // Son absence signifie donc « pas encore calculé », jamais « rien à
        // dire » — le signal est sans ambiguïté.
        const productivityPending =
            !!oralScoringEnabled &&
            !!liveReport?.statistics &&
            !liveReport?.statistics?.oral_productivity

        // Le job a épuisé ses tentatives : le score oral ne viendra jamais. Le
        // score affiché est celui du travail écrit seul — c'est la réponse
        // honnête, mais il faut le DIRE, sinon le tuteur lit un verdict sur la
        // parole de l'élève. Distinct de « l'élève a vraiment eu 0 à l'oral » :
        // même chiffre, sens opposé.
        const productivityFailed =
            !!liveReport?.statistics?.oral_productivity?.oral_scoring_failed

        // Local overlay over liveReport props — the autosave round-trip
        // doesn't propagate back into the parent's liveReports state, so we
        // keep the latest typed values here to drive the derived bilan-fait
        // state in real time.
        const [rating, setRating] = useState(liveReport?.tutor_rating ?? 0)
        const [commentValue, setCommentValue] = useState(
            liveReport?.tutor_comment ?? ""
        )
        const [recordingURI, setRecordingURI] = useState(
            usableCommentRecord(liveReport)
        )

        const hasRating = (rating ?? 0) > 0
        const hasComment = !!commentValue && commentValue.trim() !== ""
        const hasRecording = !!recordingURI
        const hasContent = hasComment || hasRecording
        // A comment on screen is not a comment on the server: while the voice
        // is on its way, or after a send failed, there is nothing to validate.
        const [commentSend, setCommentSend] = useState(null)
        // Criteria gate the CTA (live-derived). The card-collapsed state is
        // driven by manuallyValidated below, NOT by these flags.
        const canValidate = isConnected && hasRating && hasContent && !commentSend

        // Latched manual validation. Seeded ONCE from server data on first
        // render so a refresh on a partially-completed page still shows the
        // already-done students collapsed. After that, this state only flips
        // when the tutor taps the CTA — typing/recording never auto-flips it —
        // or when the comment he was redoing fails to reach the server.
        const [manuallyValidated, setManuallyValidated] = useState(() =>
            isBilanFait(liveReport)
        )
        useEffect(() => {
            if (commentSend !== COMMENT_SEND.failed || !manuallyValidated) return
            setManuallyValidated(false)
            onBilanFaitChange?.(liveReport?.id, false)
        }, [commentSend])

        const bilanFait = manuallyValidated

        // Local edit-visibility toggle: only applies when bilanFait is true.
        // While bilanFait, default is collapsed; tap row → re-expands for
        // editing. When not bilanFait, expanded view is always visible.
        const [editVisible, setEditVisible] = useState(false)

        const [expandedAxis, setExpandedAxis] = useState(null)

        const viewRef = useRef(null)
        useImperativeHandle(ref, () => ({
            measureInWindow: (cb) => {
                if (!viewRef.current) return cb?.(0, 0, 0, 0)
                // On web, findNodeHandle throws — RNWeb View exposes
                // measureInWindow directly on the ref. Don't use `??`
                // here: measureInWindow returns void and schedules cb
                // asynchronously, so `?? cb(0,0,0,0)` would fire cb a
                // second time synchronously and cause a scroll jank.
                if (IS_WEB) {
                    if (typeof viewRef.current.measureInWindow === "function") {
                        return viewRef.current.measureInWindow(cb)
                    }
                    return cb?.(0, 0, 0, 0)
                }
                const node = findNodeHandle(viewRef.current)
                if (!node) return cb?.(0, 0, 0, 0)
                UIManager.measureInWindow(node, cb)
            },
            getNode: () => viewRef.current,
            isBilanFait: () => bilanFait,
        }))

        // Prefer the qualityStudent payload when present; otherwise fall back
        // to the report-level statistics.scores so students who weren't
        // counted in occurrence-level students_scores (Celya, occ 133199)
        // still get an engagement bar.
        const scoresLike = useMemo(() => {
            const qsScores = qualityStudent?.scores
            const reportScores = liveReport?.statistics?.scores
            const qsHasDetails =
                qsScores &&
                AXES.some(
                    (axis) =>
                        Array.isArray(qsScores[axis.key]?.details) &&
                        qsScores[axis.key].details.length > 0
                )
            if (qsHasDetails) return qsScores
            if (reportScores) return reportScores
            if (qualityStudent?.total_score != null) return qsScores ?? {}
            return null
        }, [qualityStudent, liveReport])

        const totalScore = useMemo(() => {
            if (qualityStudent?.total_score != null) return qualityStudent.total_score
            return liveReport?.statistics?.scores?.total_score ?? null
        }, [qualityStudent, liveReport])

        // L'étiquette « Productivité basse » se déclenche côté serveur sur
        // `scores.productivity < 10`. Pendant l'attente du scoring oral, cette
        // valeur est le score écrit seul : l'alerte est donc prématurée et
        // disparaîtra d'elle-même une fois le composite arrivé (le mirror
        // invalide le cache quality-closure). On la tait en attendant.
        //
        // « Aucune activité » reste affichée : elle constate un fait
        // indépendant du score oral — l'élève n'a effectivement rien soumis.
        const nonOkAxes = (qualityStudent?.axes ?? [])
            .map((a) => {
                if (!productivityPending) return a
                const alerts = (a.alerts ?? []).filter(
                    (al) => al.indicator !== "productivity_score"
                )
                return alerts.length === (a.alerts ?? []).length
                    ? a
                    : { ...a, alerts, status: alerts.length ? a.status : "ok" }
            })
            .filter((a) => a.status !== "ok")

        const activitiesCount =
            missionPrescription?.activities_count ??
            missionPrescription?.activity_ids?.length ??
            0

        let helperText = null
        if (!hasRating && !hasContent)
            helperText = "Ajoute une note ⭐ et un commentaire pour valider"
        else if (!hasRating) helperText = "Ajoute une note ⭐ pour valider"
        else if (!hasContent)
            helperText = "Ajoute un commentaire (texte ou vocal) pour valider"
        else if (commentSend === COMMENT_SEND.sending)
            helperText = "Envoi du vocal…"
        else if (commentSend === COMMENT_SEND.failed)
            helperText = "Ton commentaire n'est pas encore enregistré"

        // ── Absent state ──────────────────────────────────────────────────
        if (!isConnected) {
            return (
                <View
                    ref={viewRef}
                    style={{
                        backgroundColor: theme.colors.greyBg,
                        borderRadius: 12,
                        paddingVertical: 10,
                        paddingHorizontal: 14,
                        marginBottom: 10,
                        flexDirection: "row",
                        alignItems: "center",
                    }}
                >
                    <AvatarM
                        size={32}
                        user={{
                            id: student?.id,
                            display_name: student?.display_name,
                            avatar: student?.avatar,
                        }}
                        style={{ marginRight: 10 }}
                    />
                    <TextM
                        fontSize={13}
                        color={theme.colors.grey}
                        style={{ flex: 1 }}
                    >
                        <TextM fontWeight="Bold" fontSize={13} color={theme.colors.grey}>
                            {student?.display_name ?? student?.firstname ?? "L'élève"}
                        </TextM>
                        {" est élève absent·e — pas de compte-rendu à faire"}
                    </TextM>
                </View>
            )
        }

        // ── Bilan fait collapsed strip ────────────────────────────────────
        // Renders a single row with avatar, check, name, comment preview, and
        // a severity badge if applicable. Tap row → reveals the expanded view
        // (CommentBlock stays mounted across this toggle, see below).
        const showExpanded = !bilanFait || editVisible

        const commentPreview = (() => {
            if (hasComment) {
                const s = commentValue.trim()
                return s.length > 70 ? s.slice(0, 70).trimEnd() + "…" : s
            }
            if (hasRecording) return "Compte-rendu vocal enregistré"
            return ""
        })()

        const cardWrapperStyle = {
            backgroundColor: theme.colors.white,
            borderRadius: 12,
            marginBottom: 10,
            shadowColor: "#000",
            shadowOpacity: 0.05,
            shadowRadius: 4,
            shadowOffset: { width: 0, height: 2 },
            elevation: 2,
            borderLeftWidth: 4,
            borderLeftColor: bilanFait
                ? theme.colors.success
                : theme.colors.primary,
            overflow: "hidden",
        }

        return (
            <View ref={viewRef} style={cardWrapperStyle}>
                {bilanFait && (
                    <Pressable
                        onPress={() => setEditVisible((v) => !v)}
                        style={{
                            flexDirection: "row",
                            alignItems: "center",
                            paddingVertical: 10,
                            paddingHorizontal: 12,
                            backgroundColor: theme.colors.successLight,
                        }}
                    >
                        <AvatarM
                            size={30}
                            user={{
                                id: student?.id,
                                display_name: student?.display_name,
                                avatar: student?.avatar,
                            }}
                            style={{ marginRight: 8 }}
                        />
                        <FontAwesome5
                            name="check-circle"
                            size={13}
                            color={theme.colors.success}
                            solid
                            style={{ marginRight: 6 }}
                        />
                        <View style={{ flex: 1, minWidth: 0 }}>
                            <TextM fontSize={12} color={theme.colors.dark} numberOfLines={1}>
                                <TextM
                                    fontWeight="Bold"
                                    fontSize={12}
                                    color={theme.colors.dark}
                                >
                                    Bilan fait
                                </TextM>
                                {` · ${firstName}`}
                                {commentPreview ? ` · ${commentPreview}` : ""}
                            </TextM>
                        </View>
                        <HeaderSeverityBadge nonOkAxes={nonOkAxes} />
                        <FontAwesome5
                            name={editVisible ? "chevron-up" : "chevron-down"}
                            size={11}
                            color={theme.colors.grey}
                            style={{ marginLeft: 8 }}
                        />
                    </Pressable>
                )}

                {/* The "À compléter" header (only when not in bilan-fait
                    collapsed mode). In À-compléter mode, this is the only
                    visible header and is non-collapsing. */}
                {!bilanFait && (
                    <View
                        style={{
                            flexDirection: "row",
                            alignItems: "center",
                            paddingTop: 12,
                            paddingHorizontal: 14,
                            paddingBottom: 8,
                        }}
                    >
                        <AvatarM
                            size={36}
                            user={{
                                id: student?.id,
                                display_name: student?.display_name,
                                avatar: student?.avatar,
                            }}
                            style={{ marginRight: 10 }}
                        />
                        <View style={{ flex: 1 }}>
                            <TextM
                                fontWeight="Bold"
                                fontSize={14}
                                color={theme.colors.dark}
                            >
                                {student?.display_name}
                            </TextM>
                            {student?.profile?.class ? (
                                <TextM fontSize={11} color={theme.colors.light}>
                                    {student.profile.class}
                                </TextM>
                            ) : null}
                        </View>
                        <HeaderSeverityBadge nonOkAxes={nonOkAxes} />
                    </View>
                )}

                {/* Expanded content — kept MOUNTED while bilanFait + editVisible
                    is false (hidden via display:none on web / height:0 on
                    native) so the CommentBlock's local textarea state
                    survives across the collapse/expand cycle. */}
                <View
                    style={
                        showExpanded
                            ? { paddingHorizontal: 14, paddingBottom: 12 }
                            : {
                                  height: 0,
                                  overflow: "hidden",
                                  paddingHorizontal: 14,
                              }
                    }
                    // @ts-ignore — RN-web honors display
                    {...(!showExpanded ? { pointerEvents: "none" } : {})}
                >
                    {scoresLike ? (
                        <>
                            <TappableEngagementBar
                                scoresLike={scoresLike}
                                totalScore={totalScore}
                                productivityPending={productivityPending}
                                expandedAxisKey={expandedAxis}
                                onSelectAxis={setExpandedAxis}
                            />
                            {expandedAxis ? (
                                <AxisDrillDown
                                    axisKey={expandedAxis}
                                    scoresLike={scoresLike}
                                    productivityPending={productivityPending}
                                    productivityFailed={productivityFailed}
                                />
                            ) : null}
                        </>
                    ) : null}

                    {nonOkAxes.length > 0 && (
                        <View style={{ marginBottom: 2 }}>
                            {nonOkAxes.map((axis, i) => (
                                <CollapsibleAxisAlerts key={i} axis={axis} />
                            ))}
                        </View>
                    )}

                    {missionPrescription && (
                        <View
                            style={{
                                flexDirection: "row",
                                alignItems: "center",
                                marginTop: 4,
                            }}
                        >
                            <FontAwesome5
                                name="check-circle"
                                size={11}
                                color={theme.colors.success}
                                solid
                                style={{ marginRight: 6 }}
                            />
                            <TextM
                                fontSize={11}
                                color={theme.colors.success}
                                fontWeight="Bold"
                            >
                                Travail Sam : {activitiesCount} exercice
                                {activitiesCount > 1 ? "s" : ""} envoyé
                                {activitiesCount > 1 ? "s" : ""}
                            </TextM>
                        </View>
                    )}

                    <View
                        style={{
                            height: 1,
                            backgroundColor: theme.colors.lightMore,
                            marginVertical: 8,
                        }}
                    />

                    <RatingAndTagsSection
                        liveReportId={liveReport?.id}
                        firstName={firstName}
                        initialRating={liveReport?.tutor_rating ?? 0}
                        initialTags={liveReport?.tutor_tags ?? []}
                        onChange={(r) => setRating(r)}
                    />

                    <View
                        style={{
                            height: 1,
                            backgroundColor: theme.colors.lightMore,
                            marginVertical: 10,
                        }}
                    />

                    <CommentBlock
                        liveReport={liveReport}
                        firstName={firstName}
                        isTrial={isTrial}
                        onContentChange={(v, uri) => {
                            if (typeof v === "string") setCommentValue(v)
                            if (uri !== undefined) setRecordingURI(uri)
                        }}
                        onSendChange={setCommentSend}
                    />

                    <View style={{ marginTop: 6, alignItems: "stretch" }}>
                        <FilledButtonM
                            label={`Terminé pour ${firstName}`}
                            icon="check"
                            color={theme.colors.success}
                            disabled={!canValidate}
                            onPress={() => {
                                if (!manuallyValidated) {
                                    setManuallyValidated(true)
                                    onBilanFaitChange?.(liveReport?.id, true)
                                }
                                setEditVisible(false)
                                onDone?.(liveReport?.id)
                            }}
                            style={{ width: null, marginBottom: 0 }}
                            contentStyle={{ height: 38 }}
                        />
                        {!canValidate && helperText ? (
                            <TextM
                                fontSize={11}
                                color={theme.colors.grey}
                                center
                                style={{ marginTop: 6 }}
                            >
                                {helperText}
                            </TextM>
                        ) : null}
                    </View>
                </View>
            </View>
        )
    }
)

StudentCloseOutCard.displayName = "StudentCloseOutCard"

export default StudentCloseOutCard
